import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { adminLogin, adminMe, fetchCustomerMe, logoutCustomer } from "../lib/api";

const AuthContext = createContext(null);
const TOKEN_KEY = "prestige_admin_token";

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  // customer: null = checking, false = signed-out, object = signed-in
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    let active = true;
    const check = async () => {
      if (!token) {
        setAdmin(false);
        return;
      }
      try {
        const me = await adminMe(token);
        if (active) setAdmin(me);
      } catch {
        if (active) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setAdmin(false);
        }
      }
    };
    check();
    return () => { active = false; };
  }, [token]);

  // customer session — cookie-based (Emergent Google Auth). Skip if we're mid-OAuth callback.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      // AuthCallback will exchange the session_id and set the cookie first.
      return;
    }
    let active = true;
    (async () => {
      try {
        const me = await fetchCustomerMe();
        if (active) setCustomer(me);
      } catch {
        if (active) setCustomer(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await adminLogin(email, password);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    setAdmin(data.user);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAdmin(false);
  }, []);

  const setCustomerFromCallback = useCallback((user) => {
    setCustomer(user || false);
  }, []);

  const logoutCustomerFlow = useCallback(async () => {
    try {
      await logoutCustomer();
    } catch {
      // ignore
    }
    setCustomer(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ admin, login, logout, customer, setCustomerFromCallback, logoutCustomer: logoutCustomerFlow }}
    >
      {children}
    </AuthContext.Provider>
  );
};
